import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import isEqual from 'lodash-es/isEqual';
import TextSearch from '@/shared/components/input/TextSearch';
import Toggle from 'react-toggle';
import MultiSelect, {
  addItem as addMultiSelectItem,
  removeItem as removeMultiSelectItem,
  splitItems as splitMultiSelectItems,
} from '@/shared/components/input/multiselect/MultiSelect';
import intlShape from '@/shared/utils/intlShape';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';
import LegalFormMultiSelect from '@/shared/components/input/multiselect/LegalFormMultiSelect';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import EnumMultiSelect from '@/shared/components/input/multiselect/EnumMultiSelect';
import TeamMultiSelect from '@/shared/components/input/multiselect/TeamMultiSelect';
import EndFiscalYearMultiSelect from '@/shared/components/input/multiselect/EndFiscalYearMultiSelect';
import InvoiceScheduleTypeMultiSelect from '@/shared/components/input/multiselect/InvoiceScheduleTypeMultiSelect';
import AuthService from '@/shared/services/AuthService';
import RelationTypeButtonGroup from './RelationTypeButtonGroup';
import RelationHandlingLevelButtonGroup from '@/shared/components/RelationHandlingLevelButtonGroup';
import CrmTypeButtonGroup from './CrmTypeButtonGroup';
import AssignmentTemplateSelector from './input/AssignmentTemplateSelector';
import YearSelect from './input/YearSelect';
import RelationUserSearch from './input/RelationUserSearch';
import YesNoSelect from './input/multiselect/YesNoSelect';
import YesNoSingleSelect from './input/select/YesNoSingleSelect';
import EnumSelectV2 from './input/EnumSelectV2';
import RelationGroupMultiSelect from './input/multiselect/RelationGroupMultiSelect';

import employeeProfileMessages from '../messages/employeeProfileMessages';

const messages = defineMessages({
  WithEmployeeLabels: 'With employee labels',
  WithoutEmployeeLabels: 'Without employee labels',
  OnlyActiveRelations: 'Only active relations',
  CommunicationChannelSearch: 'Communication (address, email, phone)',
  AssignedTo: 'Assigned to',
  CheckedOutUser: 'Checked out by',
  WithLabels: 'With labels',
  WithoutLabels: 'Without labels',
  CrmType: 'Crm type',
  RelationType: 'Relation type',
  LegalForms: 'Legal forms',
  OwnCompany: 'Own company',
  AssignmentTemplate: 'Assignment template',
  Sector: 'Sector',
  SocialSecretariat: 'Social secretariat',
  SocialSecurityFund: 'Social security fund',
  NotFilledOut: 'Not filled out',
  NoFinancialStatementScheme: 'No financial statement scheme',
  AccountingSoftware: 'Accounting software',
  ReceiveInvoiceSoftware: 'Receive invoice software',
  Teams: 'Teams',
  SendInvoiceStrategy: 'Send invoice strategy',
  PeppolStatus: 'PEPPOL status',
  MixedTaxPayerVatDeductionMethod: 'Mixed tax payer VAT deduction method',
  MixedTaxPayer: 'Mixed tax payer',
  SendInvoicePeppolReady: 'Send invoice peppol ready',
  EmptyMixedTaxPayerVatDeductionMethod: 'Without mixed tax payer VAT deduction method',
  EndFiscalYear: 'End fiscal year',
  OtherSoftware: 'Other software',
  SendInvoiceSoftware: 'Send invoice software',
  ReferenceYear: 'Reference year',
  WithoutReferenceYear: 'Without reference year',
  FinancialStatementScheme: 'FinancialStatementScheme',
  InvoiceSchedules: 'Invoice schedules',
  EmployeeType: 'Employee type',
  RelationGroups: 'Relation groups',
  VatPeriodicity: 'VAT periodicity',
  Pep: 'Pep',
  LinkedToPep: 'LinkedToPep',
  TotalRisk: 'AMLEndScore',
  Selfbooker: 'Selfbooker',
  EmptySendInvoiceStrategy: 'General Settings',
  RelationHandlingLevels: 'Relation Handling Levels',
});

const filterObjects = [
  { id: 'crmType', label: messages.CrmType },
  { id: 'crmTypes', label: messages.CrmType },
  { id: 'relationType', label: messages.RelationType },
  { id: 'relationTypes', label: messages.RelationType },
  { id: 'legalForms', label: messages.LegalForms },
  { id: 'responsible', label: employeeProfileMessages.Responsible },
  { id: 'assistant', label: employeeProfileMessages.Assistant },
  { id: 'associate', label: employeeProfileMessages.Associate },
  { id: 'assistantBackup', label: employeeProfileMessages.AssistantBackup },
  { id: 'billingUser', label: employeeProfileMessages.BillingUser },
  { id: 'seniorUser', label: employeeProfileMessages.SeniorUser },
  { id: 'assignedTo', label: messages.AssignedTo },
  { id: 'checkedOutUser', label: messages.CheckedOutUser },
  { id: 'withLabels', label: messages.WithLabels },
  { id: 'withoutLabels', label: messages.WithoutLabels },
  { id: 'withEmployeeLabels', label: messages.WithEmployeeLabels },
  { id: 'withoutEmployeeLabels', label: messages.WithoutEmployeeLabels },
  { id: 'relationHandlingLevel', label: messages.RelationHandlingLevels },
  { id: 'relationHandlingLevels', label: messages.RelationHandlingLevels },
  {
    id: 'ownCompanies',
    label: messages.OwnCompany,
    enabled: () => hasMultipleCompanies(),
  },
  {
    id: 'assignmentTemplateId',
    label: messages.AssignmentTemplate,
    enabled: () => hasAssignments(),
  },
  { id: 'sector', label: messages.Sector },
  { id: 'sectors', label: messages.Sector },
  { id: 'socialSecretariat', label: messages.SocialSecretariat },
  { id: 'socialSecretariats', label: messages.SocialSecretariat },
  { id: 'socialSecurityFund', label: messages.SocialSecurityFund },
  { id: 'socialSecurityFunds', label: messages.SocialSecurityFund },
  { id: 'accountingSoftware', label: messages.AccountingSoftware },
  { id: 'receiveInvoiceSoftware', label: messages.ReceiveInvoiceSoftware },
  { id: 'teamCodes', label: messages.Teams },
  { id: 'sendInvoiceStrategy', label: messages.SendInvoiceStrategy },
  { id: 'peppolStatus', label: messages.PeppolStatus },
  { id: 'mixedTaxPayerVatDeductionMethod', label: messages.MixedTaxPayerVatDeductionMethod },
  { id: 'mixedTaxPayer', label: messages.MixedTaxPayer },
  { id: 'sendInvoicePeppolReady', label: messages.SendInvoicePeppolReady },
  { id: 'endFiscalYears', label: messages.EndFiscalYear },
  { id: 'otherSoftware', label: messages.OtherSoftware },
  { id: 'sendInvoiceSoftware', label: messages.SendInvoiceSoftware },
  { id: 'financialStatementSchemes', label: messages.FinancialStatementScheme },
  { id: 'invoiceScheduleTypes', label: messages.InvoiceSchedules },
  {
    id: 'referenceYear',
    label: messages.ReferenceYear,
    enabled: () => AuthService.hasFeature('ReferenceYear'),
  },
  { id: 'employeeType', label: messages.EmployeeType },
  { id: 'relationGroups', label: messages.RelationGroups },
  { id: 'vatPeriodicities', label: messages.VatPeriodicity },
  { id: 'vatPeriodicity', label: messages.VatPeriodicity },
  { id: 'communicationChannelSearchValue', label: messages.CommunicationChannelSearch },
  { id: 'onlyActiveRelations', label: messages.OnlyActiveRelations },
  { id: 'pep', label: messages.Pep },
  { id: 'linkedToPep', label: messages.LinkedToPep },
  { id: 'totalRisk', label: messages.TotalRisk },
  { id: 'selfBooker', label: messages.Selfbooker },
];
const getEnabledFilters = dynamicFilters =>
  dynamicFilters.filter(f => {
    if (typeof f.enabled === 'undefined') return true;
    if (typeof f.enabled === 'function') return !!f.enabled();
    return !!f.enabled;
  });

export const getDynamicFiltersByKey = (keys = [], ignore = []) =>
  filterObjects.filter(x => keys.indexOf(x.id) >= 0 && ignore.indexOf(x.id) < 0);

export const getDynamicFiltersFromDefaultFilters = (defaultFilterObject = {}, ...ignore) => {
  const keys = Object.keys(defaultFilterObject);
  return getDynamicFiltersByKey(keys, ignore.flat());
};

export const initDynamicFilters = (
  intl,
  dynamicFilters = [], // Dynamic filters {id,label}
  visibleFilters = [], // Keys from storage
  filterObject = {}, // Filter object from state
  defaultFilterObject = {}, // The default filters
) =>
  getEnabledFilters(dynamicFilters)
    .map(({ id, label }) => ({
      id,
      label: label && label.id ? intl.formatMessage(label) : label?.toString(),
      selected: visibleFilters.includes(id) || !isEqual(filterObject[id], defaultFilterObject[id]),
    }))
    .sort(dynamicSort('label'));
// Use this when selecting a saved filter
export const resetDynamicFilters = (dynamicFilters, filterObject, defaultFilterObject) =>
  dynamicFilters.map(x => ({
    ...x,
    selected: x.selected || !isEqual(filterObject[x.id], defaultFilterObject[x.id]),
  }));

export const handleDynamicFiltersChanged = (
  dynamicFilters,
  item,
  selected,
  filterObject,
  defaultFilterObject,
) =>
  dynamicFilters.map(x => {
    if (item.id === 'selectall') {
      if (!selected && !isEqual(filterObject[x.id], defaultFilterObject[x.id]))
        return { ...x, selected: true }; // Keep used filter on screen
      return { ...x, selected };
    }
    if (x.id === item.id) {
      return { ...x, selected };
    }
    return x;
  });

const DynamicFilter = props => {
  const {
    type,
    filters,
    filterChanged,
    intl,
    labels = [],
    employeeLabels = [],
    renderCustomFilter,
    relationUserSearchParams = {},
    labelsIdProp = 'label',
    collClass,
  } = props;
  switch (type) {
    case 'crmType':
    case 'crmTypes':
      return (
        <div className={collClass}>
          <div className="form-group">
            <CrmTypeButtonGroup
              selectedTypes={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
            />
          </div>
        </div>
      );
    case 'relationType':
    case 'relationTypes':
      return (
        <div className={collClass}>
          <div className="form-group">
            <RelationTypeButtonGroup
              selectedTypes={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
            />
          </div>
        </div>
      );
    case 'legalForms':
      return (
        <div className={collClass}>
          <div className="form-group">
            <LegalFormMultiSelect
              selection={filters.legalForms}
              onChange={selection => filterChanged({ legalForms: selection })}
              showWithoutOption
            />
          </div>
        </div>
      );
    case 'responsible':
      return (
        <div className={collClass}>
          <div className="form-group">
            <RelationUserSearch
              {...relationUserSearchParams}
              onChange={userId => filterChanged({ responsible: userId })}
              value={filters.responsible}
              placeholder={intl.formatMessage(employeeProfileMessages.FilterResponsible)}
            />
          </div>
        </div>
      );
    case 'assistant':
      return (
        <div className={collClass}>
          <div className="form-group">
            <RelationUserSearch
              {...relationUserSearchParams}
              onChange={userId => filterChanged({ assistant: userId })}
              value={filters.assistant}
              placeholder={intl.formatMessage(employeeProfileMessages.FilterAssistant)}
            />
          </div>
        </div>
      );
    case 'associate':
      return (
        <div className={collClass}>
          <div className="form-group">
            <RelationUserSearch
              {...relationUserSearchParams}
              onChange={userId => filterChanged({ associate: userId })}
              value={filters.associate}
              placeholder={intl.formatMessage(employeeProfileMessages.FilterAssociate)}
            />
          </div>
        </div>
      );
    case 'assistantBackup':
      return (
        <div className={collClass}>
          <div className="form-group">
            <RelationUserSearch
              {...relationUserSearchParams}
              onChange={userId => filterChanged({ assistantBackup: userId })}
              value={filters.assistantBackup}
              placeholder={intl.formatMessage(employeeProfileMessages.FilterAssistantBackup)}
            />
          </div>
        </div>
      );
    case 'billingUser':
      return (
        <div className={collClass}>
          <div className="form-group">
            <RelationUserSearch
              {...relationUserSearchParams}
              onChange={userId => filterChanged({ billingUser: userId })}
              value={filters.billingUser}
              placeholder={intl.formatMessage(employeeProfileMessages.FilterBillingUser)}
            />
          </div>
        </div>
      );
    case 'seniorUser':
      return (
        <div className={collClass}>
          <div className="form-group">
            <RelationUserSearch
              {...relationUserSearchParams}
              onChange={userId => filterChanged({ seniorUser: userId })}
              value={filters.seniorUser}
              placeholder={intl.formatMessage(employeeProfileMessages.FilterSeniorUser)}
            />
          </div>
        </div>
      );
    case 'assignedTo':
      return (
        <div className={collClass}>
          <div className="form-group">
            <RelationUserSearch
              withUnassigned={false}
              {...relationUserSearchParams}
              onChange={userId => filterChanged({ assignedTo: userId })}
              value={filters.assignedTo}
              placeholder={intl.formatMessage(messages.AssignedTo)}
            />
          </div>
        </div>
      );
    case 'checkedOutUser':
      return (
        <div className={collClass}>
          <div className="form-group">
            <RelationUserSearch
              {...relationUserSearchParams}
              onChange={userId => filterChanged({ checkedOutUser: userId })}
              value={filters.checkedOutUser}
              placeholder={intl.formatMessage(messages.CheckedOutUser)}
              withUnassigned={false}
              withCurrentUser={false}
            />
          </div>
        </div>
      );
    case 'withLabels': {
      const [selectedLabels, availableLabels] = splitMultiSelectItems(
        labels,
        filters.withLabels,
        labelsIdProp,
      );
      return (
        <div className={collClass}>
          <div className="form-group">
            <MultiSelect
              selectedItem={selectedLabels}
              onItemRemoved={item =>
                filterChanged({
                  withLabels: removeMultiSelectItem(filters.withLabels, item, labelsIdProp),
                })
              }
              onItemSelected={item =>
                filterChanged({
                  withLabels: addMultiSelectItem(filters.withLabels, item, labelsIdProp),
                })
              }
              items={availableLabels}
              placeholder={intl.formatMessage(messages.WithLabels)}
              onFilterBehaviourChanged={filterBehaviour => {
                filterChanged({
                  filterBehaviours: {
                    withLabels: filterBehaviour,
                  },
                });
              }}
              filterBehaviour={filters.filterBehaviours?.withLabels}
              withFilterBehaviour={typeof filters.filterBehaviours?.withLabels !== 'undefined'}
            />
          </div>
        </div>
      );
    }
    case 'withEmployeeLabels': {
      const [selectedLabels, availableLabels] = splitMultiSelectItems(
        employeeLabels,
        filters.withEmployeeLabels,
        labelsIdProp,
      );
      return (
        <div className={collClass}>
          <div className="form-group">
            <MultiSelect
              selectedItem={selectedLabels}
              onItemRemoved={item =>
                filterChanged({
                  withEmployeeLabels: removeMultiSelectItem(
                    filters.withEmployeeLabels,
                    item,
                    labelsIdProp,
                  ),
                })
              }
              onItemSelected={item =>
                filterChanged({
                  withEmployeeLabels: addMultiSelectItem(
                    filters.withEmployeeLabels,
                    item,
                    labelsIdProp,
                  ),
                })
              }
              items={availableLabels}
              placeholder={intl.formatMessage(messages.WithEmployeeLabels)}
              onFilterBehaviourChanged={filterBehaviour => {
                filterChanged({
                  filterBehaviours: {
                    withEmployeeLabels: filterBehaviour,
                  },
                });
              }}
              filterBehaviour={filters.filterBehaviours?.withEmployeeLabels}
              withFilterBehaviour={
                typeof filters.filterBehaviours?.withEmployeeLabels !== 'undefined'
              }
            />
          </div>
        </div>
      );
    }
    case 'withoutLabels': {
      const [selectedLabels, availableLabels] = splitMultiSelectItems(
        labels,
        filters.withoutLabels,
        labelsIdProp,
      );

      return (
        <div className={collClass}>
          <div className="form-group">
            <MultiSelect
              selectedItem={selectedLabels}
              onItemRemoved={item =>
                filterChanged({
                  withoutLabels: removeMultiSelectItem(filters.withoutLabels, item, labelsIdProp),
                })
              }
              onItemSelected={item =>
                filterChanged({
                  withoutLabels: addMultiSelectItem(filters.withoutLabels, item, labelsIdProp),
                })
              }
              items={availableLabels}
              placeholder={intl.formatMessage(messages.WithoutLabels)}
            />
          </div>
        </div>
      );
    }
    case 'withoutEmployeeLabels': {
      const [selectedLabels, availableLabels] = splitMultiSelectItems(
        employeeLabels,
        filters.withoutEmployeeLabels,
        labelsIdProp,
      );

      return (
        <div className={collClass}>
          <div className="form-group">
            <MultiSelect
              selectedItem={selectedLabels}
              onItemRemoved={item =>
                filterChanged({
                  withoutEmployeeLabels: removeMultiSelectItem(
                    filters.withoutEmployeeLabels,
                    item,
                    labelsIdProp,
                  ),
                })
              }
              onItemSelected={item =>
                filterChanged({
                  withoutEmployeeLabels: addMultiSelectItem(
                    filters.withoutEmployeeLabels,
                    item,
                    labelsIdProp,
                  ),
                })
              }
              items={availableLabels}
              placeholder={intl.formatMessage(messages.WithoutEmployeeLabels)}
            />
          </div>
        </div>
      );
    }
    case 'ownCompanies': {
      return hasMultipleCompanies() ? (
        <div className={collClass}>
          <div className="form-group">
            <CompanyMultiSelect
              selection={filters.ownCompanies}
              onChange={companies => filterChanged({ ownCompanies: companies })}
            />
          </div>
        </div>
      ) : null;
    }
    case 'assignmentTemplateId': {
      return hasAssignments() ? (
        <div className={collClass}>
          <div className="form-group">
            <AssignmentTemplateSelector
              value={filters.assignmentTemplateId}
              onChange={id => filterChanged({ assignmentTemplateId: id || null })}
              showDefaultAssignment
            />
          </div>
        </div>
      ) : null;
    }
    case 'sector':
    case 'sectors':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="sector"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.Sector)}
            />
          </div>
        </div>
      );
    case 'socialSecretariat':
    case 'socialSecretariats':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="socialsecretariat"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.SocialSecretariat)}
              customItems={[
                {
                  id: -1,
                  label: intl.formatMessage(messages.NotFilledOut),
                },
              ]}
            />
          </div>
        </div>
      );
    case 'socialSecurityFund':
    case 'socialSecurityFunds':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="socialsecurityfund"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.SocialSecurityFund)}
              customItems={[
                {
                  id: -1,
                  label: intl.formatMessage(messages.NotFilledOut),
                },
              ]}
            />
          </div>
        </div>
      );
    case 'accountingSoftware':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="accountingsoftware"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.AccountingSoftware)}
            />
          </div>
        </div>
      );
    case 'receiveInvoiceSoftware':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="receiveInvoiceSoftware"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.ReceiveInvoiceSoftware)}
              customItems={[
                {
                  id: -1,
                  label: intl.formatMessage(messages.NotFilledOut),
                },
              ]}
            />
          </div>
        </div>
      );
    case 'financialStatementSchemes':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="financialStatementScheme"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.FinancialStatementScheme)}
              customItems={[
                {
                  id: -1,
                  label: intl.formatMessage(messages.NoFinancialStatementScheme),
                },
              ]}
            />
          </div>
        </div>
      );
    case 'sendInvoiceStrategy':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="sendInvoiceStrategy"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.SendInvoiceStrategy)}
              customItems={[
                {
                  id: -1,
                  label: intl.formatMessage(messages.EmptySendInvoiceStrategy),
                },
              ]}
            />
          </div>
        </div>
      );
    case 'invoiceScheduleTypes':
      return (
        <div className={collClass}>
          <div className="form-group">
            <InvoiceScheduleTypeMultiSelect
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
            />
          </div>
        </div>
      );
    case 'peppolStatus':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="relationPeppolStatus"
              selection={filters[type]}
              sortById
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.PeppolStatus)}
            />
          </div>
        </div>
      );
    case 'teamCodes': {
      return (
        <div className={collClass}>
          <div className="form-group">
            <TeamMultiSelect
              selection={filters[type]}
              onChange={value => filterChanged({ [type]: value })}
              idProp="code"
            />
          </div>
        </div>
      );
    }
    case 'mixedTaxPayerVatDeductionMethod':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="mixedTaxPayerVatDeductionMethod"
              selection={filters[type]}
              customItems={[
                {
                  id: -1,
                  label: intl.formatMessage(messages.EmptyMixedTaxPayerVatDeductionMethod),
                },
              ]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.MixedTaxPayerVatDeductionMethod)}
            />
          </div>
        </div>
      );
    case 'mixedTaxPayer':
      return (
        <div className={collClass}>
          <div className="form-group">
            <YesNoSelect
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.MixedTaxPayer)}
            />
          </div>
        </div>
      );
    case 'sendInvoicePeppolReady':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="PeppolReadyStatus"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.SendInvoicePeppolReady)}
            />
          </div>
        </div>
      );
    case 'endFiscalYears': {
      return (
        <div className={collClass}>
          <div className="form-group">
            <EndFiscalYearMultiSelect
              selection={filters.endFiscalYears}
              onChange={endFiscalYears => filterChanged({ endFiscalYears })}
            />
          </div>
        </div>
      );
    }
    case 'otherSoftware':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="othersoftware"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.OtherSoftware)}
              customItems={[
                {
                  id: -1,
                  label: intl.formatMessage(messages.NotFilledOut),
                },
              ]}
            />
          </div>
        </div>
      );
    case 'sendInvoiceSoftware':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="sendInvoiceSoftware"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.SendInvoiceSoftware)}
              customItems={[
                {
                  id: -1,
                  label: intl.formatMessage(messages.NotFilledOut),
                },
              ]}
            />
          </div>
        </div>
      );
    case 'referenceYear':
      return (
        <div className={collClass}>
          <div className="form-group">
            <YearSelect
              value={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.ReferenceYear)}
              withoutOption={intl.formatMessage(messages.WithoutReferenceYear)}
            />
          </div>
        </div>
      );
    case 'employeeType':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumSelectV2
              enumType="employeeType"
              value={filters[type]}
              allowNull
              placeholder={intl.formatMessage(messages.EmployeeType)}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
            />
          </div>
        </div>
      );
    case 'relationGroups':
      return (
        <div className={collClass}>
          <div className="form-group">
            <RelationGroupMultiSelect
              selection={filters[type]}
              placeholder={intl.formatMessage(messages.RelationGroups)}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
            />
          </div>
        </div>
      );
    case 'vatPeriodicities':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="vatPeriodicity"
              selection={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.VatPeriodicity)}
            />
          </div>
        </div>
      );
    case 'communicationChannelSearchValue':
      return (
        <div className={collClass}>
          <div className="form-group">
            <TextSearch
              value={filters[type]}
              className="form-control"
              placeholder={intl.formatMessage(messages.CommunicationChannelSearch)}
              onChange={value => filterChanged({ [type]: value })}
            />
          </div>
        </div>
      );
    case 'onlyActiveRelations':
      return (
        <div className={collClass}>
          <div className="form-group">
            <label className="react-toggle-wrapper margin10T toggle-lg">
              <Toggle
                checked={filters[type]}
                onChange={e => filterChanged({ [type]: !!e.target.checked })}
                icons={false}
              />
              <FormattedMessage className="label-text" {...messages.OnlyActiveRelations} />
            </label>
          </div>
        </div>
      );

    case 'pep':
      return (
        <div className={collClass}>
          <div className="form-group">
            <YesNoSingleSelect
              value={filters[type]}
              onChange={val => filterChanged({ [type]: val })}
              placeholder={intl.formatMessage(messages.Pep)}
            />
          </div>
        </div>
      );

    case 'linkedToPep':
      return (
        <div className={collClass}>
          <div className="form-group">
            <YesNoSingleSelect
              value={filters[type]}
              onChange={val => filterChanged({ [type]: val })}
              placeholder={intl.formatMessage(messages.LinkedToPep)}
            />
          </div>
        </div>
      );

    case 'totalRisk':
      return (
        <div className={collClass}>
          <div className="form-group">
            <EnumMultiSelect
              type="aMLRiskLevel"
              selection={filters[type]}
              sortById
              onChange={value => {
                filterChanged({ [type]: value });
              }}
              placeholder={intl.formatMessage(messages.TotalRisk)}
            />
          </div>
        </div>
      );

    case 'selfBooker':
      return (
        <div className={collClass}>
          <div className="form-group">
            <YesNoSingleSelect
              value={filters[type]}
              onChange={val => filterChanged({ [type]: val })}
              placeholder={intl.formatMessage(messages.Selfbooker)}
            />
          </div>
        </div>
      );

    case 'relationHandlingLevel':
    case 'relationHandlingLevels':
      return (
        <div className={collClass}>
          <div className="form-group">
            <RelationHandlingLevelButtonGroup
              selectedHandlingLevels={filters[type]}
              onChange={value => {
                filterChanged({ [type]: value });
              }}
            />
          </div>
        </div>
      );

    default:
      return renderCustomFilter(props);
  }
};
DynamicFilter.propTypes = {
  type: PropTypes.string.isRequired,
  filters: PropTypes.shape({}).isRequired,
  filterChanged: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  labels: PropTypes.arrayOf(PropTypes.shape({})),
  renderCustomFilter: PropTypes.func,
  relationUserSearchParams: PropTypes.shape({}),
  labelsIdProp: PropTypes.string,
  collClass: PropTypes.string,
};
DynamicFilter.defaultProps = {
  labels: [],
  renderCustomFilter: props => null, // eslint-disable-line
  relationUserSearchParams: {},
  labelsIdProp: 'label',
  collClass: 'col-lg-4 col-sm-6',
};
export default DynamicFilter;
